/* eslint-disable react-refresh/only-export-components */ // Provider file includes hooks/utilities used internally; exporting only the component would require refactor
import { haveDataInSections, isSbConnectUser } from '@/helpers/helper';
import { useAuth } from '@/hooks';
import { useAsync } from '@/hooks/useAsync';
import { INote, IPrevNotes } from '@/interfaces/noteInterface';
import { createAINote, getAINote, getPreviousHistory } from '@/service';
import * as Sentry from '@sentry/react';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

const { logger } = Sentry;

export type NoteStatus =
  | 'idle'
  | 'creating'
  | 'loading'
  | 'polling'
  | 'completed'
  | 'error';

interface NoteContextType {
  // Session state (for portal page - current/latest note)
  sessionNote: INote | null;
  sessionNoteId: string | null;
  sessionStatus: NoteStatus;

  // History state (for history page - selected historical note)
  currentNote: INote | null;
  currentNoteId: string | null;
  status: NoteStatus;

  // Shared state
  error: string | null;
  history: IPrevNotes | null;
  patientId: string | null;

  // Computed
  isLoading: boolean;
  isCompleted: boolean;
  isSessionLoading: boolean;
  isSessionCompleted: boolean;
  isSbConnect: boolean;

  // Actions
  createNote: (
    audioBlob: Blob,
    examTime: string,
    onSessionNoteCreated?: (noteId: string) => void
  ) => Promise<boolean>;
  loadNote: (caseId: string) => Promise<void>;
  getHistory: (page?: number) => Promise<IPrevNotes | null>;
  reset: () => void;
  resetSession: () => void;
  resetAll: () => void;
  setPatientId: (id: string | null) => void;
}

const NoteContext = createContext<NoteContextType | null>(null);

export const useNote = () => {
  const context = useContext(NoteContext);
  if (!context) {
    throw new Error(
      'useNote must be used within NoteProvider. Make sure the component using useNote is wrapped by <NoteProvider>.'
    );
  }
  return context;
};

interface NoteProviderProps {
  children: ReactNode;
}

export const NoteProvider = ({ children }: NoteProviderProps) => {
  // Session state (for portal page)
  const [sessionNote, setSessionNote] = useState<INote | null>(null);
  const [sessionNoteId, setSessionNoteId] = useState<string | null>(null);
  const [sessionStatus, setSessionStatus] = useState<NoteStatus>('idle');

  // History state (for history page)
  const [currentNote, setCurrentNote] = useState<INote | null>(null);
  const [currentNoteId, setCurrentNoteId] = useState<string | null>(null);
  const [status, setStatus] = useState<NoteStatus>('idle');

  // Shared state
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<IPrevNotes | null>(null);
  const [patientId, setPatientId] = useState<string | null>(null);

  // Polling resources
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollAbortRef = useRef<AbortController | null>(null);
  const pendingNotesRef = useRef<Set<string>>(new Set());

  // Token from Auth
  const { token } = useAuth();

  // Check if user is sb-connect
  const isSbConnect = isSbConnectUser(token);

  // Helper function to check if note is truly completed
  // For sb-connect users, we check ecwStatus for both fresh and history notes
  const isNoteCompleted = useCallback(
    (note: INote | null): boolean => {
      if (!note) return false;

      // Check if note has content
      const hasContent =
        (note as Partial<INote>).hasResult === true ||
        haveDataInSections(note as INote);

      if (!hasContent) return false;

      // For sb-connect users, also check ecwStatus
      if (isSbConnect) {
        const draftVerified = (note as Partial<INote>).draft_verified === true;
        if (draftVerified) return true;

        const ecwStatus = note.ecwStatus?.toLowerCase();
        return ecwStatus === 'completed';
      }

      // For non-sb-connect users, having content is enough
      return true;
    },
    [isSbConnect]
  );

  // Computed values
  const isLoading = status === 'loading' || status === 'polling';
  const isCompleted = status === 'completed';
  const isSessionLoading =
    sessionStatus === 'creating' || sessionStatus === 'polling';
  const isSessionCompleted = sessionStatus === 'completed';

  // No external initialize required; token is synced from AuthContext

  // Async helpers for cancellation and cleaner calls (internal only)
  const getHistoryAsync = useAsync<IPrevNotes, [number]>(
    async (signal, page) => {
      if (!token) throw new Error('No authentication token');
      return await getPreviousHistory(token, page, signal ?? null);
    }
  );

  const loadNoteAsync = useAsync<INote | null, [string]>(
    async (signal, caseId) => {
      if (!token) throw new Error('No authentication token');
      const res = await getAINote(caseId, token, signal ?? null);
      // Map service error objects to null for simpler consumers
      const maybe = res as unknown as { error?: unknown } | INote | null;
      if (maybe && 'error' in maybe) return null;
      return res as INote;
    }
  );

  const createNoteAsync = useAsync<
    string | false,
    [Blob, string, string | null]
  >(async (_signal, audioBlob, examTime, patientIdArg) => {
    if (!token) throw new Error('No authentication token');
    return await createAINote(
      audioBlob,
      examTime,
      token,
      patientIdArg ?? undefined
    );
  });

  // Reset history state (for history page)
  const reset = useCallback(() => {
    setCurrentNote(null);
    setCurrentNoteId(null);
    setStatus('idle');
    setError(null);
    // Stop polling when resetting
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    if (pollAbortRef.current) {
      pollAbortRef.current.abort();
      pollAbortRef.current = null;
    }
    pendingNotesRef.current.clear();
  }, []);

  // Reset session state (for portal page)
  const resetSession = useCallback(() => {
    setSessionNote(null);
    setSessionNoteId(null);
    setSessionStatus('idle');
    setError(null);
  }, []);

  // Reset ALL state (for logout)
  const resetAll = useCallback(() => {
    // Clear session state
    setSessionNote(null);
    setSessionNoteId(null);
    setSessionStatus('idle');

    // Clear history state
    setCurrentNote(null);
    setCurrentNoteId(null);
    setStatus('idle');

    // Clear shared state
    setError(null);
    setHistory(null);
    setPatientId(null);

    // Stop all polling
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    if (pollAbortRef.current) {
      pollAbortRef.current.abort();
      pollAbortRef.current = null;
    }
    pendingNotesRef.current.clear();
  }, []);

  // Start polling for pending notes
  const startPolling = useCallback(() => {
    if (pollTimeoutRef.current || pendingNotesRef.current.size === 0) return;

    const scheduleNext = () => {
      pollTimeoutRef.current = setTimeout(pollOnce, 5000);
    };

    const pollOnce = async () => {
      pollTimeoutRef.current = null;
      if (!token || pendingNotesRef.current.size === 0) {
        // Nothing to do; ensure controllers are cleaned up
        if (pollAbortRef.current) {
          pollAbortRef.current.abort();
          pollAbortRef.current = null;
        }
        return;
      }

      // Abort previous in-flight requests (if any) and start a new controller
      if (pollAbortRef.current) pollAbortRef.current.abort();
      const controller = new AbortController();
      pollAbortRef.current = controller;

      try {
        const promises = Array.from(pendingNotesRef.current).map(
          async caseId => {
            if (!token) return;
            try {
              const note = await getAINote(caseId, token, controller.signal);

              // Check for red_flag (invalid audio) without coder verification
              const redFlag = (note as Partial<INote>)?.red_flag === true;
              const draftVerified =
                (note as Partial<INote>)?.draft_verified === true;

              if (note && redFlag && !draftVerified) {
                // Audio processing failed (silence, corrupted, etc.)
                logger.warn(
                  logger.fmt`Audio processing failed for note: ${caseId}`,
                  {
                    caseid: caseId,
                    reason: 'red_flag',
                  }
                );

                pendingNotesRef.current.delete(caseId);

                // Mark as failed in history list
                setHistory(prev => {
                  if (!prev || !prev.notes) return prev;
                  const caseIdNum = Number(caseId);
                  let changed = false;
                  const updatedNotes = prev.notes.map(n => {
                    if (n.caseid === caseIdNum) {
                      changed = true;
                      return isSbConnect
                        ? { ...n, ecwStatus: 'Failed' }
                        : { ...n, cleanup_job_status: 'Failed' };
                    }
                    return n;
                  });
                  return changed ? { ...prev, notes: updatedNotes } : prev;
                });

                // Update session note if this is the one we're waiting for
                setSessionNoteId(prevId => {
                  if (prevId === caseId) {
                    setSessionStatus('error');
                    setError(
                      'Unable to process audio: The recording appears to be silent or corrupted. Please record a new audio file with clear speech.'
                    );
                  }
                  return prevId;
                });

                // Update current note if this is the one we're waiting for (history)
                setCurrentNoteId(prevId => {
                  if (prevId === caseId) {
                    setStatus('error');
                    setError(
                      'Unable to process audio: The recording appears to be silent or corrupted. Please record a new audio file with clear speech.'
                    );
                  }
                  return prevId;
                });
              } else if (note && isNoteCompleted(note as INote)) {
                // Note is complete (has content AND ecwStatus is 'Completed' for sb-connect users)
                logger.info(logger.fmt`Note processing completed: ${caseId}`, {
                  caseid: caseId,
                  ecw_status: (note as INote).ecwStatus,
                  cleanup_job_status: (note as INote).cleanup_job_status,
                  is_sb_connect: isSbConnect,
                });

                delete note.section_list;
                pendingNotesRef.current.delete(caseId);

                // Reflect completion in history list if present
                setHistory(prev => {
                  if (!prev || !prev.notes) return prev;
                  const caseIdNum = Number(caseId);
                  let changed = false;
                  const updatedNotes = prev.notes.map(n => {
                    const alreadyCompleted = isSbConnect
                      ? n.ecwStatus === 'Completed'
                      : n.cleanup_job_status === 'Completed';
                    if (n.caseid === caseIdNum && !alreadyCompleted) {
                      changed = true;
                      return isSbConnect
                        ? { ...n, ecwStatus: 'Completed' }
                        : { ...n, cleanup_job_status: 'Completed' };
                    }
                    return n;
                  });
                  return changed ? { ...prev, notes: updatedNotes } : prev;
                });

                // Background refresh of the current history page to sync server-side fields
                try {
                  const lastPageRaw =
                    typeof window !== 'undefined'
                      ? window.localStorage.getItem('history:lastPage')
                      : null;
                  const page = lastPageRaw ? parseInt(lastPageRaw, 10) || 1 : 1;
                  if (token) {
                    void getPreviousHistory(
                      token,
                      page,
                      controller.signal
                    ).then(data => {
                      if (data) setHistory(data);
                    });
                  }
                } catch (e) {
                  logger.error(
                    'Background history refresh failed after polling completion',
                    {
                      error: String(e),
                    }
                  );
                }

                // Update session note if this is the one we're waiting for
                setSessionNoteId(prevId => {
                  if (prevId === caseId) {
                    setSessionNote(note);
                    setSessionStatus('completed');
                  }
                  return prevId;
                });

                // Update current note if this is the one we're waiting for (history)
                setCurrentNoteId(prevId => {
                  if (prevId === caseId) {
                    setCurrentNote(note);
                    setStatus('completed');
                  }
                  return prevId;
                });
              } else {
                // Not complete yet: only mark session as polling if this pending caseId is the current session note
                setSessionNoteId(prevId => {
                  if (prevId === caseId) {
                    setSessionStatus('polling');
                  }
                  return prevId;
                });
              }
            } catch (error) {
              const maybe = error as { name?: unknown };
              if (typeof maybe.name === 'string' && maybe.name === 'AbortError')
                return;
              logger.error('Error polling note', {
                caseid: caseId,
                error: String(error),
              });
              pendingNotesRef.current.delete(caseId);
            }
          }
        );

        await Promise.all(promises);
      } finally {
        // Schedule next tick if we still have pending items
        if (pendingNotesRef.current.size > 0) {
          scheduleNext();
        } else {
          // Clean up controller if no longer needed
          if (pollAbortRef.current) {
            pollAbortRef.current.abort();
            pollAbortRef.current = null;
          }
        }
      }
    };

    // Kick off first poll immediately
    void pollOnce();
  }, [token, isNoteCompleted, isSbConnect]);

  // Create a new note (for session/portal page)
  const createNote = useCallback(
    async (
      audioBlob: Blob,
      examTime: string,
      onSessionNoteCreated?: (noteId: string) => void
    ): Promise<boolean> => {
      if (!token) {
        setSessionStatus('error');
        setError('No authentication token');
        return false;
      }

      setSessionStatus('creating');
      setError(null);

      try {
        const caseId = await createNoteAsync.run(
          audioBlob,
          examTime,
          patientId
        );

        if (caseId) {
          const caseIdStr = caseId.toString();
          setSessionNoteId(caseIdStr);
          setSessionStatus('polling');

          // Notify about new session note creation
          if (onSessionNoteCreated) {
            onSessionNoteCreated(caseIdStr);
          }

          // Try to get the note immediately
          try {
            const note = await getAINote(caseIdStr, token);

            // Check for red_flag (invalid audio)
            if (note && (note as Partial<INote>).red_flag === true) {
              // Audio processing failed (silence, corrupted, etc.)
              logger.warn(
                logger.fmt`Session note failed - invalid audio: ${caseIdStr}`,
                {
                  caseid: caseIdStr,
                  reason: 'red_flag',
                }
              );

              // Update history to reflect error
              setHistory(prev => {
                if (!prev || !prev.notes) return prev;
                const caseIdNum = Number(caseIdStr);
                let changed = false;
                const updatedNotes = prev.notes.map(n => {
                  if (n.caseid === caseIdNum) {
                    changed = true;
                    return isSbConnect
                      ? { ...n, ecwStatus: 'Failed' }
                      : { ...n, cleanup_job_status: 'Failed' };
                  }
                  return n;
                });
                return changed ? { ...prev, notes: updatedNotes } : prev;
              });

              setSessionStatus('error');
              setError(
                'Unable to process audio: The recording appears to be silent or corrupted. Please record a new audio file with clear speech.'
              );
            } else if (note && isNoteCompleted(note as INote)) {
              // Note is already complete (has content AND ecwStatus is 'Completed' for sb-connect users)
              logger.info(
                logger.fmt`Session note created successfully: ${caseIdStr}`,
                {
                  caseid: caseIdStr,
                  immediate: true,
                  ecw_status: (note as INote).ecwStatus,
                  cleanup_job_status: (note as INote).cleanup_job_status,
                  is_sb_connect: isSbConnect,
                }
              );

              delete note.section_list;
              setSessionNote(note);
              setSessionStatus('completed');
            } else {
              // Note is still processing, add to pending and start polling
              logger.debug(
                logger.fmt`Session note still processing, starting polling: ${caseIdStr}`,
                {
                  caseid: caseIdStr,
                }
              );

              // Do not set a placeholder session note; keep loading state
              setSessionStatus('polling');
              pendingNotesRef.current.add(caseIdStr);
              startPolling();
            }
          } catch (error) {
            // If immediate fetch fails, still start polling
            logger.warn('Initial note fetch failed, starting polling', {
              caseid: caseIdStr,
              error: String(error),
            });
            // Keep polling and loading; do not set placeholder note
            setSessionStatus('polling');
            pendingNotesRef.current.add(caseIdStr);
            startPolling();
          }

          return true;
        } else {
          logger.error('Failed to create note - no case ID returned');
          setSessionStatus('error');
          setError('Failed to create note');
          return false;
        }
      } catch (error) {
        logger.error('Error creating note', {
          error: String(error),
          message: (error as Error).message,
        });

        Sentry.captureException(error, {
          level: 'error',
          tags: { operation: 'create_note' },
        });

        setSessionStatus('error');
        setError('Error creating note: ' + (error as Error).message);
        return false;
      }
    },
    [
      startPolling,
      token,
      createNoteAsync,
      patientId,
      isNoteCompleted,
      isSbConnect,
    ]
  );

  // Load a specific note
  const loadNote = useCallback(
    async (caseId: string): Promise<void> => {
      if (!token) {
        setStatus('error');
        setError('No authentication token');
        return;
      }

      // CRITICAL FIX: Prevent duplicate calls for the same note
      setCurrentNoteId(prevId => {
        if (prevId === caseId) {
          // Already loading/loaded this note, don't make another call
          return prevId;
        }

        // New note, proceed with loading
        setStatus('loading');
        setError(null);

        // Make the API call
        (async () => {
          try {
            const note = await loadNoteAsync.run(caseId);

            if (note) {
              // Check for red_flag (invalid audio) without coder verification
              const redFlag = (note as Partial<INote>)?.red_flag === true;
              const draftVerified =
                (note as Partial<INote>)?.draft_verified === true;

              if (redFlag && !draftVerified) {
                // Audio processing failed (silence, corrupted, etc.)
                logger.warn(
                  logger.fmt`Historical note has red flag: ${caseId}`,
                  {
                    caseid: caseId,
                    reason: 'red_flag',
                    context: 'history_view',
                  }
                );

                setCurrentNote(note as INote);
                setStatus('error');
                setError(
                  'Unable to process audio: The recording appears to be silent or corrupted. Please record a new audio file with clear speech.'
                );

                // Mark as failed in history list
                setHistory(prev => {
                  if (!prev || !prev.notes) return prev;
                  const caseIdNum = Number(caseId);
                  let changed = false;
                  const updatedNotes = prev.notes.map(n => {
                    if (n.caseid === caseIdNum) {
                      changed = true;
                      return isSbConnect
                        ? { ...n, ecwStatus: 'Failed' }
                        : { ...n, cleanup_job_status: 'Failed' };
                    }
                    return n;
                  });
                  return changed ? { ...prev, notes: updatedNotes } : prev;
                });
              } else if (isNoteCompleted(note as INote)) {
                // Note is complete (has content AND ecwStatus is 'Completed' for sb-connect users)
                delete (note as unknown as Record<string, unknown>)[
                  'section_list'
                ];
                setCurrentNote(note as INote);
                setStatus('completed');

                // Reflect completion in history list if present
                setHistory(prev => {
                  if (!prev || !prev.notes) return prev;
                  const caseIdNum = Number(caseId);
                  let changed = false;
                  const updatedNotes = prev.notes.map(n => {
                    const alreadyCompleted = isSbConnect
                      ? n.ecwStatus === 'Completed'
                      : n.cleanup_job_status === 'Completed';
                    if (n.caseid === caseIdNum && !alreadyCompleted) {
                      changed = true;
                      return isSbConnect
                        ? { ...n, ecwStatus: 'Completed' }
                        : { ...n, cleanup_job_status: 'Completed' };
                    }
                    return n;
                  });
                  return changed ? { ...prev, notes: updatedNotes } : prev;
                });

                // Background refresh of the current history page to sync server-side fields
                try {
                  const lastPageRaw =
                    typeof window !== 'undefined'
                      ? window.localStorage.getItem('history:lastPage')
                      : null;
                  const page = lastPageRaw ? parseInt(lastPageRaw, 10) || 1 : 1;
                  if (token) {
                    void getPreviousHistory(token, page).then(data => {
                      if (data) setHistory(data);
                    });
                  }
                } catch (e) {
                  logger.error(
                    'Background history refresh failed after loadNote completion',
                    {
                      error: String(e),
                    }
                  );
                }
              } else {
                // Check if note is pending/processing
                const notePartial = note as Partial<INote>;
                const isProcessing =
                  notePartial.caseid && Object.keys(note as object).length <= 3; // Only basic fields
                if (isProcessing) {
                  setCurrentNote(note as INote);
                  setStatus('polling');
                  // Add to pending notes for polling
                  pendingNotesRef.current.add(caseId);
                  startPolling();
                } else {
                  // Historical note with content - mark as completed
                  setCurrentNote(note as INote);
                  setStatus('completed');
                }
              }
            }
          } catch (error) {
            const maybe = error as { name?: unknown; message?: unknown };
            if (
              typeof maybe?.name === 'string' &&
              maybe.name === 'AbortError'
            ) {
              // Ignore aborted requests (likely due to rapid selection changes)
              return;
            }
            setStatus('error');
            setError(
              'Error loading note: ' + String(maybe?.message ?? 'unknown error')
            );
          }
        })();

        return caseId;
      });
    },
    [startPolling, token, loadNoteAsync, isNoteCompleted, isSbConnect]
  );

  // Get history
  const getHistory = useCallback(
    async (page: number = 1): Promise<IPrevNotes | null> => {
      if (!token) {
        setStatus('error');
        setError('No authentication token');
        return null;
      }

      try {
        const historyData = await getHistoryAsync.run(page);
        setHistory(historyData);
        return historyData;
      } catch (error) {
        const maybe = error as { name?: unknown; message?: unknown };
        if (typeof maybe?.name === 'string' && maybe.name === 'AbortError') {
          // Ignore aborts (e.g., rapid page changes or unmount)
          return null;
        }
        setStatus('error');
        setError(
          'Error fetching history: ' + String(maybe?.message ?? 'unknown error')
        );
        return null;
      }
    },
    [token, getHistoryAsync]
  ); // No dependencies - uses refs and state setters

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
      if (pollAbortRef.current) pollAbortRef.current.abort();
    };
  }, []);

  const value: NoteContextType = {
    // Session state
    sessionNote,
    sessionNoteId,
    sessionStatus,
    // History state
    currentNote,
    currentNoteId,
    status,
    // Shared state
    error,
    history,
    patientId,
    // Computed
    isLoading,
    isCompleted,
    isSessionLoading,
    isSessionCompleted,
    isSbConnect,
    // Actions
    createNote,
    loadNote,
    getHistory,
    reset,
    resetSession,
    resetAll,
    setPatientId,
  };

  return <NoteContext.Provider value={value}>{children}</NoteContext.Provider>;
};
